import React from "react";
import type {
  IPaymentGatewayChoice,
  PaymentGateway,
} from "@/src/shared/features/payment/types";

const NETWORKS = [
  { src: "payment_network_visa.svg", className: "h-3 w-[37px]" },
  { src: "payment_network_mastercard.svg", className: "h-3.5 w-[23px]" },
  { src: "payment_network_amex.svg", className: "h-[18px] w-[18px]" },
  { src: "payment_network_nagad.png", className: "h-[18px] w-[18px]" },
  {
    src: "payment_network_rocket.png",
    className: "h-[18px] w-[27px] rounded object-cover",
  },
  { src: "payment_network_upay.png", className: "h-[18px] w-[18px]" },
];

const MethodRow: React.FC<{
  gateway: PaymentGateway;
  choice: IPaymentGatewayChoice;
  badge?: string;
  children: React.ReactNode;
}> = ({ gateway, choice, badge, children }) => (
  <button
    type="button"
    role="radio"
    aria-checked={choice.gateway === gateway}
    onClick={() => choice.select(gateway)}
    className="flex w-full items-center gap-3 px-4 py-3.5 text-left"
  >
    <img
      src={`/icons/confirm-pay/${gateway === "ssl" ? "payment_other_methods" : "payment_bkash"}.png`}
      alt=""
      className="h-6 w-6 shrink-0 rounded object-cover"
    />
    <span className="min-w-0 flex-1">{children}</span>
    {badge && (
      <span className="shrink-0 rounded-full bg-[#F7F7F7] px-3 py-1.5 text-12 font-semibold leading-4 text-ink-secondary">
        {badge}
      </span>
    )}
    <img
      src={`/icons/confirm-pay/report_radio_${choice.gateway === gateway ? "on" : "off"}.svg`}
      alt=""
      aria-hidden
      className="h-6 w-6 shrink-0"
    />
  </button>
);

/**
 * The app's `SelectPaymentMethodPage` card: a saved bKash number ("Primary") first, the other
 * methods with their networks, then bKash.
 */
const PaymentMethodList: React.FC<{ choice: IPaymentGatewayChoice }> = ({
  choice,
}) => {
  const plainBkash = (
    <MethodRow key="bkash" gateway="bkash" choice={choice}>
      <span className="text-16 font-medium leading-6 text-ink">bKash</span>
    </MethodRow>
  );
  const rows = [
    choice.hasSavedBkash ? (
      <MethodRow
        key="saved"
        gateway="savedBkash"
        choice={choice}
        badge="Primary"
      >
        <span className="block text-16 font-medium leading-6 text-ink">
          bKash
        </span>
        <span className="mt-0.5 block text-14 leading-5 text-ink-secondary">
          {choice.savedBkashNumber}
        </span>
      </MethodRow>
    ) : (
      plainBkash
    ),
    <MethodRow key="ssl" gateway="ssl" choice={choice}>
      <span className="block text-16 font-medium leading-6 text-ink">
        Other payment methods
      </span>
      <span className="mt-2 flex items-center gap-2">
        {NETWORKS.map((network) => (
          <img
            key={network.src}
            src={`/icons/confirm-pay/${network.src}`}
            alt=""
            className={network.className}
          />
        ))}
      </span>
    </MethodRow>,
    ...(choice.hasSavedBkash ? [plainBkash] : []),
  ];

  return (
    <div
      role="radiogroup"
      aria-label="Payment method"
      className="rounded-xl border border-[#EBEBEB] bg-white px-4"
    >
      {rows.map((row, index) => (
        <React.Fragment key={row.key}>
          {index > 0 && (
            <div className="py-1">
              <div className="h-px bg-[#EBEBEB]" />
            </div>
          )}
          {row}
        </React.Fragment>
      ))}
    </div>
  );
};

export default PaymentMethodList;
